import React from 'react';
import { MapPin, Calendar, Users } from 'lucide-react';

const DestinationSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  russianDestinations,
  seasons
}) => {
  // Массив месяцев для выбора
  const months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  // Определение текущего сезона
  const getCurrentSeason = () => {
    if (formData.travelingSeason !== 'auto') {
      const season = seasons.find(s => s.id === formData.travelingSeason);
      return season ? season.name : 'Не определен';
    }
    
    if (formData.autoDetectedSeason) {
      const season = seasons.find(s => s.id === formData.autoDetectedSeason);
      return season ? season.name : 'Не определен';
    }
    
    return 'Определяется автоматически';
  };

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Выбор направления */}
        <div className="col-span-full">
          <label htmlFor="destination" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <MapPin className="w-4 h-4 mr-2 text-purple-500" />
            Место назначения
          </label>
          <select
            id="destination"
            name="destination"
            value={formData.destination}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.destination ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите направление</option>
            <optgroup label="Города">
              {russianDestinations
                .filter(dest => dest.type === 'city')
                .map(dest => (
                  <option key={dest.id} value={dest.id}>{dest.name}</option>
                ))
              }
            </optgroup>
            <optgroup label="Курорты">
              {russianDestinations
                .filter(dest => dest.type === 'resort')
                .map(dest => (
                  <option key={dest.id} value={dest.id}>{dest.name}</option>
                ))
              }
            </optgroup>
            <optgroup label="Природные достопримечательности">
              {russianDestinations
                .filter(dest => dest.type === 'nature')
                .map(dest => (
                  <option key={dest.id} value={dest.id}>{dest.name}</option>
                ))
              }
            </optgroup>
            <optgroup label="Туристические маршруты">
              {russianDestinations
                .filter(dest => dest.type === 'route')
                .map(dest => (
                  <option key={dest.id} value={dest.id}>{dest.name}</option>
                ))
              }
            </optgroup>
          </select>
          {errors.destination && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.destination}
            </p>
          )}
        </div>

        {/* Город отправления (опционально) */}
        <div>
          <label htmlFor="originCity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Город отправления (опционально)
          </label>
          <input
            type="text"
            id="originCity"
            name="originCity"
            value={formData.originCity}
            onChange={handleInputChange}
            className="form-input w-full"
            placeholder="Введите город отправления"
          />
        </div>

        {/* Продолжительность поездки */}
        <div>
          <label htmlFor="tripDuration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-purple-500" />
            Продолжительность (дней)
          </label>
          <input
            type="number"
            id="tripDuration"
            name="tripDuration"
            value={formData.tripDuration}
            onChange={handleInputChange}
            min="1"
            max="90"
            className={`form-input w-full ${errors.tripDuration ? 'input-error' : ''}`}
            placeholder="Например: 7"
          />
          {errors.tripDuration && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.tripDuration}
            </p>
          )}
        </div>

        {/* Количество путешественников */}
        <div>
          <label htmlFor="travelers" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Users className="w-4 h-4 mr-2 text-purple-500" />
            Количество путешественников
          </label>
          <input
            type="number"
            id="travelers"
            name="travelers"
            value={formData.travelers}
            onChange={handleInputChange}
            min="1"
            max="20"
            className={`form-input w-full ${errors.travelers ? 'input-error' : ''}`}
            placeholder="Например: 2"
          />
          {errors.travelers && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.travelers}
            </p>
          )}
        </div>

        {/* Месяц поездки */}
        <div>
          <label htmlFor="travelMonth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Месяц поездки
          </label>
          <select
            id="travelMonth"
            name="travelMonth"
            value={formData.travelMonth}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {months.map((month, index) => (
              <option key={index} value={index}>{month}</option>
            ))}
          </select>
        </div>

        {/* Сезон поездки */}
        <div>
          <label htmlFor="travelingSeason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Туристический сезон
          </label>
          <div className="flex items-center">
            <select
              id="travelingSeason"
              name="travelingSeason"
              value={formData.travelingSeason}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              <option value="auto">Определить автоматически</option>
              <option value="low">Низкий сезон (скидки)</option>
              <option value="shoulder">Межсезонье</option>
              <option value="high">Высокий сезон</option>
              <option value="peak">Пиковый сезон</option>
            </select>
          </div>
          {formData.travelingSeason === 'auto' && formData.autoDetectedSeason && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Определен сезон: {getCurrentSeason()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DestinationSection;